import React, { useEffect, useState } from 'react';
import SharingSettings from '@mysimpledb/shared/components/Account/SharingSettings';
import useGetSharingSettings from '@mysimpledb/shared/hooks/useGetSharingSettings';
import useUpdateSharingSettings from '@mysimpledb/shared/hooks/useUpdateSharingSettings';

const SharingSettingsWeb = () => {
  const token = localStorage.getItem('token');
  const {
    data: rawData,
    loading,
    error,
    refetch,
  } = useGetSharingSettings(token);
  const { updateSettings: updateSharingSettings } =
    useUpdateSharingSettings(token);

  const [data, setData] = useState({
    sharedName: '',
    shareProfilePicture: false,
    sharing: 'none',
    viewing: 'none',
    sharedEmails: [],
    allowedTypeIds: [],
    allowedSubtypeIds: [],
    typeMatchMode: 'any',
  });
  const [errors, setErrors] = useState({});
  const [types, setTypes] = useState([]);

  // Normalize snake_case → camelCase for form
  useEffect(() => {
    if (rawData) {
      setData({
        sharedName: rawData.settings.shared_name || '',
        shareProfilePicture: rawData.settings.share_profile_picture || false,
        sharing: rawData.settings.sharing || 'none',
        viewing: rawData.settings.viewing || 'none',
        sharedEmails: rawData.settings.shared_emails || [],
        allowedTypeIds: rawData.settings.allowed_type_ids || [],
        allowedSubtypeIds: rawData.settings.allowed_subtype_ids || [],
        typeMatchMode:
          rawData.settings.viewing_type_match_mode === 'all' ? 'all' : 'any',
      });

      setTypes(rawData.types);
    }
  }, [rawData]);

  const handleFieldChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const errs = {};
    if (
      ['friends', 'everyone'].includes(data.sharing) &&
      !data.sharedName.trim()
    ) {
      errs.sharedName = 'Shared name is required.';
    }
    if (data.sharedName.length > 15) {
      errs.sharedName = 'Shared name must be 15 characters or fewer.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async ({
    allowedTypeIds = [],
    allowedSubtypeIds = [],
  } = {}) => {
    if (!validate()) return;

    const payload = {
      sharing: data.sharing,
      viewing: data.viewing,
      shared_name: data.sharedName,
      share_profile_picture: data.shareProfilePicture,
      emails: data.sharedEmails.join(','),
      typeIds: allowedTypeIds,
      subtypeIds: allowedSubtypeIds,
      typeMatchMode: data.typeMatchMode,
    };

    await updateSharingSettings(payload);
    refetch(); // Refresh after save
  };

  return (
    <>
      <section
        style={{
          marginBottom: 24,
          padding: '20px 24px',
          border: '1px solid #d9dee8',
          borderRadius: 12,
          background: '#fff',
        }}
      >
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>
          Entry Type Matching
        </h2>
        <p style={{ margin: '0 0 16px', color: '#596579' }}>
          Choose how your selected user types and subtypes filter shared
          entries.
        </p>

        <label style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <input
            type="radio"
            name="typeMatchMode"
            value="any"
            checked={data.typeMatchMode === 'any'}
            onChange={() => handleFieldChange('typeMatchMode', 'any')}
          />
          <span>
            <strong>Match any selected type</strong>
            <small style={{ display: 'block', color: '#6b7280' }}>
              Show a broader range of entries matching at least one selection.
            </small>
          </span>
        </label>

        <label style={{ display: 'flex', gap: 10 }}>
          <input
            type="radio"
            name="typeMatchMode"
            value="all"
            checked={data.typeMatchMode === 'all'}
            onChange={() => handleFieldChange('typeMatchMode', 'all')}
          />
          <span>
            <strong>Match all selected types</strong>
            <small style={{ display: 'block', color: '#6b7280' }}>
              Show more specific entries associated with every selection.
            </small>
          </span>
        </label>

        <p style={{ margin: '16px 0 0', color: '#596579', fontSize: 13 }}>
          A top-level type includes all of its subtypes only when no specific
          subtype is selected beneath it.
        </p>
      </section>

      <SharingSettings
        data={data}
        types={types}
        initialTypeIds={data.allowedTypeIds || []}
        initialSubtypeIds={data.allowedSubtypeIds || []}
        errors={errors}
        loading={loading}
        onFieldChange={handleFieldChange}
        onSave={handleSave}
      />
    </>
  );
};

export default SharingSettingsWeb;
